import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions, storage } from "../../../firebase.js";
import { normalizeBanquet } from "../model/banquetModel.js";
import { normalizeAssignedBanquetIds } from "../model/banquetOwnerModel.js";
import { assertCanManageBanquet, BANQUET_COLLECTION, BANQUET_DRAFT_COLLECTION, canManageAllBanquets } from "./banquetAccessRules.js";

const banquetCollectionRef = collection(db, BANQUET_COLLECTION);
const banquetDocRef = (banquetId) => doc(db, BANQUET_COLLECTION, banquetId);
const banquetDraftDocRef = (banquetId) => doc(db, BANQUET_DRAFT_COLLECTION, banquetId);
const numberOrNull = (value) => (value === "" || value === null || value === undefined ? null : Number(value));
const numberOrZero = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
const stripUndefined = (value) => {
  if (Array.isArray(value)) {
    return value.map(stripUndefined);
  }

  if (value && typeof value === "object" && typeof value.toDate !== "function") {
    return Object.entries(value).reduce((clean, [key, entry]) => {
      if (entry !== undefined) {
        clean[key] = stripUndefined(entry);
      }
      return clean;
    }, {});
  }

  return value;
};

const MAX_EDIT_HISTORY_ENTRIES = 20;

const buildEditHistoryEntry = (actingOwner) => ({
  email: actingOwner?.email || "unknown",
  timestamp: new Date().toISOString(),
});

const appendEditHistory = (existingHistory, entry) => {
  const history = Array.isArray(existingHistory) ? existingHistory : [];
  return [...history, entry].slice(-MAX_EDIT_HISTORY_ENTRIES);
};

export const buildOwnerDraftBanquetId = (owner) => {
  const uid = owner?.uid || auth.currentUser?.uid;
  if (!uid) {
    throw new Error("Please login again.");
  }

  return `owner-draft-${uid}`;
};

const getBanquetSnapshotById = async (banquetId) => {
  const snapshot = await getDoc(banquetDocRef(banquetId));
  if (!snapshot.exists()) {
    return null;
  }

  return normalizeBanquet({
    id: snapshot.id,
    ...snapshot.data(),
  });
};

const getBanquetDraftSnapshotById = async (banquetId) => {
  const draftRef = banquetDraftDocRef(banquetId);
  console.info("[banquet-debug][draft-read] exact draft doc path being read", draftRef.path);
  const snapshot = await getDoc(draftRef);
  console.info("[banquet-debug][draft-read] getDoc exists", {
    path: draftRef.path,
    exists: snapshot.exists(),
    title: snapshot.data()?.title,
    name: snapshot.data()?.name,
    banquetName: snapshot.data()?.banquetName,
  });
  if (!snapshot.exists()) {
    return null;
  }

  return normalizeBanquet({
    id: snapshot.id,
    ...snapshot.data(),
    sourceBanquetId: snapshot.data().sourceBanquetId || snapshot.id,
  });
};

export const getBanquetDraftById = async ({ banquetId }) => {
  if (!banquetId) {
    const error = new Error("Banquet draft id is required.");
    error.code = "banquet-owner/missing-draft-id";
    throw error;
  }

  return getBanquetDraftSnapshotById(banquetId);
};

const hydrateBanquetWithSavedDraft = async (banquet) => {
  if (!banquet?.id) {
    return banquet || null;
  }

  const savedDraft = await getBanquetDraftSnapshotById(banquet.id);
  if (!savedDraft) {
    return banquet;
  }

  return normalizeBanquet({
    ...banquet,
    ...savedDraft,
    id: banquet.id,
    sourceBanquetId: savedDraft.sourceBanquetId || banquet.id,
  });
};

const buildDraftBanquetForOwner = ({ banquetId, owner, draftWarning = "" }) => {
  const payload = {
    id: banquetId,
    title: "",
    name: "",
    slug: banquetId,
    status: "draft",
    ownerEmail: owner.email || "",
    ownerId: owner.uid,
    assignedUserIds: owner.uid ? [owner.uid] : [],
    assignedUserEmails: owner.email ? [owner.email] : [],
    managerEmails: owner.role === "banquet_manager" && owner.email ? [owner.email] : [],
    staffEmails: owner.role === "banquet_staff" && owner.email ? [owner.email] : [],
    createdByOwner: true,
    googleMapsUrl: "",
    draftWarning,
    // "Create Banquet User" -> "New Banquet" mode: no banquet doc exists yet at account
    // creation time, so this in-memory placeholder (the very first thing the new owner's
    // dashboard renders) is where the "new accounts start locked" default actually takes
    // effect — mirrors the explicit detailsLocked: true set on real docs in
    // createBanquetOwnerAccount's "Link to Existing Banquet" path (functions/index.js).
    detailsLocked: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  return normalizeBanquet(payload);
};

const getOrCreateAssignedBanquet = async ({ banquetId, owner }) => {
  const existingBanquet = await getBanquetSnapshotById(banquetId);
  if (existingBanquet) {
    return hydrateBanquetWithSavedDraft(existingBanquet);
  }

  return hydrateBanquetWithSavedDraft(buildDraftBanquetForOwner({
    banquetId,
    owner,
    draftWarning: "Assigned banquet record is not created yet. Saving draft will create/repair this banquet profile.",
  }));
};

export const getBanquetByOwner = async ({ ownerId }) => {
  if (!ownerId) {
    const error = new Error("Owner id is required.");
    error.code = "banquet-owner/missing-owner-id";
    throw error;
  }

  const byOwnerIdQuery = query(banquetCollectionRef, where("ownerId", "==", ownerId), limit(1));
  const byOwnerIdSnapshot = await getDocs(byOwnerIdQuery);

  if (!byOwnerIdSnapshot.empty) {
    const firstDoc = byOwnerIdSnapshot.docs[0];
    return normalizeBanquet({
      id: firstDoc.id,
      ...firstDoc.data(),
    });
  }

  return null;
};

export const getBanquetsForOwner = async ({ owner }) => {
  if (!owner?.uid) {
    const error = new Error("Authenticated owner profile is required.");
    error.code = "banquet-owner/missing-owner";
    throw error;
  }

  console.info("[banquet-debug][reload-service] owner uid", owner.uid);

  if (canManageAllBanquets(owner)) {
    const snapshot = await getDocs(banquetCollectionRef);
    const banquets = snapshot.docs.map((item) =>
      normalizeBanquet({
        id: item.id,
        ...item.data(),
      })
    );
    return banquets.length
      ? banquets
      : [
          buildDraftBanquetForOwner({
            banquetId: buildOwnerDraftBanquetId(owner),
            owner,
            draftWarning: "No banquet records found yet. You can create a test draft from this editor.",
          }),
        ];
  }

  const assignedBanquetIds = normalizeAssignedBanquetIds(owner);
  console.info("[banquet-debug][reload-service] generated/assigned banquet id", {
    generatedBanquetId: buildOwnerDraftBanquetId(owner),
    assignedBanquetIds,
  });

  if (!assignedBanquetIds.length) {
    const draftBanquetId = buildOwnerDraftBanquetId(owner);
    return [
      await hydrateBanquetWithSavedDraft(buildDraftBanquetForOwner({
        banquetId: draftBanquetId,
        owner,
        draftWarning: "No assigned banquet found. You can still start a new banquet profile draft. DecorZoom admin will review and assign/publish it.",
      })),
    ];
  }

  const assignedBanquets = await Promise.all(
    assignedBanquetIds.map((banquetId) => getOrCreateAssignedBanquet({ banquetId, owner }))
  );

  return assignedBanquets.filter(Boolean);
};

const overlayPendingEditPreview = async (banquet) => {
  if (!banquet.hasPendingEdit) {
    return banquet;
  }

  const draftSnapshot = await getDoc(banquetDraftDocRef(banquet.id));
  if (!draftSnapshot.exists()) {
    return banquet;
  }

  return normalizeBanquet({
    ...banquet,
    ...draftSnapshot.data(),
    id: banquet.id,
    status: banquet.status,
    isActive: banquet.isActive,
    hasPendingEdit: banquet.hasPendingEdit,
    pendingEditBy: banquet.pendingEditBy,
    pendingEditSubmittedAt: banquet.pendingEditSubmittedAt,
    submittedAt: banquet.submittedAt,
    approvedAt: banquet.approvedAt,
    approvedBy: banquet.approvedBy,
  });
};

export const getPendingBanquetSubmissions = async ({ actingOwner }) => {
  if (!canManageAllBanquets(actingOwner)) {
    const error = new Error("Only a super admin can review banquet submissions.");
    error.code = "banquet-owner/super-admin-only";
    throw error;
  }

  const snapshot = await getDocs(query(banquetCollectionRef, orderBy("submittedAt", "desc")));
  const banquets = snapshot.docs
    .map((item) => normalizeBanquet({ id: item.id, ...item.data() }))
    .filter((banquet) => ["pending_review", "pending_approval", "approved", "published", "rejected"].includes(banquet.status));

  return Promise.all(banquets.map(overlayPendingEditPreview));
};

export const listBanquetsForLinking = async ({ actingOwner }) => {
  if (!canManageAllBanquets(actingOwner)) {
    const error = new Error("Only a super admin can browse existing banquets.");
    error.code = "banquet-owner/super-admin-only";
    throw error;
  }

  const snapshot = await getDocs(banquetCollectionRef);
  return snapshot.docs
    .map((item) => {
      const data = item.data();
      return {
        id: item.id,
        name: data.name || data.title || item.id,
        area: data.area || data.locality || data.locationDetails?.locality || "",
        city: data.city || data.locationDetails?.city || "",
        address: data.locationDetails?.fullAddress || data.address?.line1 || "",
        status: data.status || "",
        ownerId: data.ownerId || "",
        ownerEmail: data.ownerEmail || "",
        detailsLocked: Boolean(data.detailsLocked),
      };
    })
    .sort((left, right) => left.name.localeCompare(right.name));
};

// Super-admin-only toggle for the owner/manager login dashboard's "Details Lock" — locking
// only restricts that banquet's OWN login-based dashboard (BanquetOwnerDashboardPage.jsx) to
// the Availability Calendar section; it never restricts a super admin, and it has no effect
// on the separate owner-fill link flow (OwnerFillFormPage.jsx), which is unlocked by design.
export const setBanquetDetailsLocked = async ({ actingOwner, banquetId, detailsLocked }) => {
  if (!canManageAllBanquets(actingOwner)) {
    const error = new Error("Only a super admin can lock or unlock banquet details.");
    error.code = "banquet-owner/super-admin-only";
    throw error;
  }
  if (!banquetId) {
    const error = new Error("Banquet id is required.");
    error.code = "banquet-owner/missing-banquet-id";
    throw error;
  }

  await updateDoc(banquetDocRef(banquetId), {
    detailsLocked: Boolean(detailsLocked),
    updatedAt: serverTimestamp(),
  });
};

export const getBanquetById = async ({ banquetId }) => {
  if (!banquetId) {
    const error = new Error("Banquet id is required.");
    error.code = "banquet-owner/missing-banquet-id";
    throw error;
  }

  return getBanquetSnapshotById(banquetId);
};

export const updateBanquetBasicInfo = async ({ actingOwner, banquet }) => {
  const normalizedBanquet = normalizeBanquet(banquet);
  const currentBanquet = await getBanquetSnapshotById(normalizedBanquet.id);

  if (!currentBanquet) {
    const error = new Error("Banquet not found.");
    error.code = "banquet-owner/banquet-not-found";
    throw error;
  }

  assertCanManageBanquet(actingOwner, currentBanquet);

  await updateDoc(banquetDocRef(normalizedBanquet.id), {
    name: banquet.name || normalizedBanquet.title,
    slug: normalizedBanquet.slug,
    title: normalizedBanquet.title,
    ownerEmail: normalizedBanquet.ownerEmail,
    ownerName: banquet.ownerName || normalizedBanquet.ownerName || actingOwner?.fullName || "",
    phone: banquet.phone || "",
    whatsappNumber: banquet.whatsappNumber || "",
    email: banquet.email || "",
    address: normalizedBanquet.address,
    pincode: normalizedBanquet.address?.pincode || banquet.pincode || "",
    locality: normalizedBanquet.locality,
    area: banquet.area || "",
    city: normalizedBanquet.city,
    shortDescription: banquet.shortDescription || "",
    description: banquet.description || "",
    googleRating: numberOrNull(banquet.googleRating),
    googleRatingsTotal: numberOrNull(banquet.googleRatingsTotal),
    googlePlaceId: banquet.googlePlaceId || "",
    website: banquet.website || "",
    openingHours: Array.isArray(banquet.openingHours) ? banquet.openingHours : [],
    googlePhotos: Array.isArray(banquet.googlePhotos) ? banquet.googlePhotos : [],
    venueType: banquet.venueType || "",
    basicDetails: normalizedBanquet.basicDetails,
    contactDetails: normalizedBanquet.contactDetails,
    locationDetails: normalizedBanquet.locationDetails,
    featured: Boolean(banquet.featured),
    isActive: normalizedBanquet.isActive,
    pricing: normalizedBanquet.pricing,
    vegPrice: numberOrNull(banquet.vegPrice),
    nonVegPrice: numberOrNull(banquet.nonVegPrice),
    guestRange: normalizedBanquet.guestRange,
    guestMin: numberOrNull(banquet.guestMin),
    guestMax: numberOrNull(banquet.guestMax),
    latitude: numberOrNull(banquet.latitude),
    longitude: numberOrNull(banquet.longitude),
    mapUrl: banquet.mapUrl || "",
    googleMapsUrl: banquet.googleMapsUrl || banquet.mapUrl || "",
    googleMapsEmbedUrl: banquet.googleMapsEmbedUrl || "",
    createdByOwner: Boolean(banquet.createdByOwner),
    createdBy: banquet.createdBy || normalizedBanquet.createdBy || "",
    updatedAt: serverTimestamp(),
  });

  return getBanquetSnapshotById(normalizedBanquet.id);
};

export const updateBanquetImages = async ({ actingOwner, banquetId, images }) => {
  if (!Array.isArray(images)) {
    const error = new Error("Images must be an array.");
    error.code = "banquet-owner/invalid-images";
    throw error;
  }

  const currentBanquet = await getBanquetSnapshotById(banquetId);

  if (!currentBanquet) {
    const error = new Error("Banquet not found.");
    error.code = "banquet-owner/banquet-not-found";
    throw error;
  }

  assertCanManageBanquet(actingOwner, currentBanquet);

  await updateDoc(banquetDocRef(banquetId), {
    images,
    coverImage: images[0] || "",
    updatedAt: serverTimestamp(),
  });

  return getBanquetSnapshotById(banquetId);
};

const uploadBanquetMedia = ({ banquetId, file, mediaRole = "gallery", folder = "gallery", fallbackExtension = "jpg", onProgress }) =>
  new Promise((resolve, reject) => {
    if (!banquetId || !file) {
      reject(new Error("Banquet id and media file are required."));
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      reject(new Error("Please sign in again before uploading banquet media."));
      return;
    }

    const extension = (file.name.split(".").pop() || fallbackExtension).toLowerCase().replace(/[^a-z0-9]/g, "") || fallbackExtension;
    const safeRole = String(mediaRole || folder)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || folder;
    const safeFileName = file.name
      .replace(/\.[^.]+$/, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80) || "media";
    const filePath = `banquets/${banquetId}/${safeRole}/${safeFileName}.${extension}`;
    if (process.env.NODE_ENV !== "production") {
      console.info("[banquet-media-upload]", {
        uid: currentUser.uid,
        draftId: banquetId,
        uploadPath: filePath,
        expectedDraftPath: `owner-draft-${currentUser.uid}`,
      });
    }
    const uploadTask = uploadBytesResumable(ref(storage, filePath), file, {
      contentType: file.type,
    });

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        if (typeof onProgress === "function") {
          onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
        }
      },
      reject,
      async () => {
        // getDownloadURL can fail after a successful upload (network blip, transient
        // permission error). Without this try/catch the rejection here is unhandled and
        // this Promise never resolves or rejects — callers await it forever, which is
        // what left the owner dashboard's Submit button stuck on "Submitting...".
        try {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadUrl);
        } catch (downloadUrlError) {
          reject(downloadUrlError);
        }
      }
    );
  });

export const uploadBanquetImage = ({ banquetId, file, imageRole = "gallery", onProgress }) =>
  uploadBanquetMedia({
    banquetId,
    file,
    mediaRole: imageRole === "cover" ? "cover" : "gallery",
    folder: "gallery",
    fallbackExtension: file?.type === "image/webp" ? "webp" : "jpg",
    onProgress,
  });

export const uploadBanquetVideo = ({ banquetId, file, onProgress }) =>
  uploadBanquetMedia({
    banquetId,
    file,
    mediaRole: "teaser-video",
    folder: "teaser-video",
    fallbackExtension: "mp4",
    onProgress,
  });

export const uploadBanquetPackageDocument = ({ banquetId, file, onProgress }) =>
  uploadBanquetMedia({
    banquetId,
    file,
    mediaRole: "packages-menu",
    folder: "packages-menu",
    fallbackExtension: "pdf",
    onProgress,
  });

export const updateBanquetAvailability = async ({ actingOwner, banquetId, availabilityCalendar, bookedSlots, bookedDates }) => {
  const currentBanquet = await getBanquetSnapshotById(banquetId);

  if (!currentBanquet) {
    const error = new Error("Banquet not found.");
    error.code = "banquet-owner/banquet-not-found";
    throw error;
  }

  assertCanManageBanquet(actingOwner, currentBanquet);

  await updateDoc(banquetDocRef(banquetId), {
    availabilityCalendar: availabilityCalendar || currentBanquet.availabilityCalendar,
    bookedSlots: bookedSlots || currentBanquet.bookedSlots,
    bookedDates: bookedDates || availabilityCalendar?.bookedDates || currentBanquet.bookedDates || {},
    updatedAt: serverTimestamp(),
  });

  return getBanquetSnapshotById(banquetId);
};

export const saveBanquetDraft = async ({ actingOwner, banquet, syncCanonical = false }) => {
  const normalizedBanquet = normalizeBanquet(banquet);
  if (!normalizedBanquet.id) {
    const error = new Error("Banquet id is required before saving a draft.");
    error.code = "banquet-owner/missing-banquet-id";
    throw error;
  }

  const ownerId = normalizedBanquet.ownerId || actingOwner?.uid || "";
  const ownerEmail = normalizedBanquet.ownerEmail || actingOwner?.email || "";
  const editorEmail = actingOwner?.email || ownerEmail || "";
  const editHistoryEntry = buildEditHistoryEntry(actingOwner);
  const draftRef = banquetDraftDocRef(normalizedBanquet.id);
  const existingDraftSnapshot = await getDoc(draftRef).catch(() => null);
  const existingDraftEditHistory = existingDraftSnapshot?.exists() ? existingDraftSnapshot.data().editHistory : [];
  const draftPayload = stripUndefined({
    ...normalizedBanquet,
    ownerId,
    ownerEmail,
    assignedUserIds: Array.isArray(normalizedBanquet.assignedUserIds) && normalizedBanquet.assignedUserIds.length
      ? normalizedBanquet.assignedUserIds
      : ownerId
        ? [ownerId]
        : [],
    assignedUserEmails: Array.isArray(normalizedBanquet.assignedUserEmails) && normalizedBanquet.assignedUserEmails.length
      ? normalizedBanquet.assignedUserEmails
      : ownerEmail
        ? [ownerEmail]
        : [],
    status: "draft",
    submittedForApproval: false,
    draftUpdatedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastEditedBy: editorEmail,
    editHistory: appendEditHistory(existingDraftEditHistory, editHistoryEntry),
  });
  const draftWritePayload = {
    ...draftPayload,
    sourceBanquetId: normalizedBanquet.id,
    draftOwnerId: actingOwner?.uid || ownerId,
    // lastEditedAt is applied after stripUndefined so the Firestore serverTimestamp()
    // sentinel reaches Firestore intact (stripUndefined would otherwise flatten it).
    lastEditedAt: serverTimestamp(),
  };

  console.info("[banquet-debug][saveBanquetDraft before setDoc]", {
    path: draftRef.path,
    payloadKeys: Object.keys(draftWritePayload),
    ownerUid: actingOwner?.uid || ownerId,
    title: draftWritePayload.title,
    name: draftWritePayload.name,
    banquetName: draftWritePayload.banquetName,
  });
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("banquet-debug-save", {
      detail: {
        setDocStatus: "writing",
        message: `Writing to ${draftRef.path}`,
        path: draftRef.path,
      },
    }));
  }

  try {
    await setDoc(
      draftRef,
      draftWritePayload,
      { merge: true }
    );
  } catch (setDocError) {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("banquet-debug-save", {
        detail: {
          setDocStatus: "error",
          message: "setDoc error",
          path: draftRef.path,
          error: `${setDocError.code || "no-code"}: ${setDocError.message || String(setDocError)}`,
        },
      }));
    }
    throw setDocError;
  }
  console.info("[banquet-debug][saveBanquetDraft after setDoc]", {
    setDocCompleted: true,
    savedDocId: normalizedBanquet.id,
    path: draftRef.path,
  });
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("banquet-debug-save", {
      detail: {
        setDocStatus: "success",
        message: "setDoc success",
        path: draftRef.path,
      },
    }));
  }

  if (!syncCanonical) {
    // Booking-date availability is the one field that must never wait on admin approval —
    // stale availability on the public site risks a double-booking. Every other edit for an
    // already-published banquet only lands in banquetDrafts here and waits for
    // hasPendingEdit review (see submitBanquetForApprovalOnce), but the calendar goes
    // straight to the live doc regardless of which save action (autosave or submit)
    // triggered this write, as long as a live doc already exists to update.
    const liveBanquetForAvailability = await getBanquetSnapshotById(normalizedBanquet.id).catch(() => null);
    if (liveBanquetForAvailability) {
      // updateBanquetAvailability re-checks assertCanManageBanquet itself; the fetch above
      // only exists to skip this write entirely when no live doc exists yet.
      await updateBanquetAvailability({
        actingOwner,
        banquetId: normalizedBanquet.id,
        availabilityCalendar: normalizedBanquet.availabilityCalendar,
        bookedSlots: normalizedBanquet.bookedSlots,
        bookedDates: normalizedBanquet.bookedDates || normalizedBanquet.availabilityCalendar?.bookedDates || {},
      });
    }

    return normalizeBanquet({
      ...normalizedBanquet,
      ...draftWritePayload,
      id: normalizedBanquet.id,
      sourceBanquetId: normalizedBanquet.id,
    });
  }

  let currentBanquet = await getBanquetSnapshotById(normalizedBanquet.id);

  if (!currentBanquet) {
    const canCreateOwnDraft =
      canManageAllBanquets(actingOwner) ||
      Boolean(actingOwner?.uid && (normalizedBanquet.ownerId === actingOwner.uid || normalizedBanquet.createdByOwner));

    if (!canCreateOwnDraft) {
      const error = new Error("Banquet not found.");
      error.code = "banquet-owner/banquet-not-found";
      throw error;
    }

    await setDoc(
      banquetDocRef(normalizedBanquet.id),
      {
        ...draftPayload,
        ownerId,
        ownerEmail,
        createdByOwner: true,
        status: "draft",
        submittedForApproval: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    currentBanquet = await getBanquetSnapshotById(normalizedBanquet.id);
  }

  assertCanManageBanquet(actingOwner, currentBanquet);

  if (currentBanquet.status === "rejected") {
    await updateDoc(banquetDocRef(normalizedBanquet.id), {
      status: "draft",
      submittedForApproval: false,
      updatedAt: serverTimestamp(),
    });
    currentBanquet = { ...currentBanquet, status: "draft", submittedForApproval: false };
  }

  await updateBanquetBasicInfo({ actingOwner, banquet: { ...banquet, status: currentBanquet.status || "draft" } });
  await updateBanquetImages({ actingOwner, banquetId: normalizedBanquet.id, images: normalizedBanquet.images });
  await updateBanquetDetails({ actingOwner, banquet });
  await updateBanquetAvailability({
    actingOwner,
    banquetId: normalizedBanquet.id,
    availabilityCalendar: normalizedBanquet.availabilityCalendar,
    bookedSlots: normalizedBanquet.bookedSlots,
    bookedDates: normalizedBanquet.bookedDates || normalizedBanquet.availabilityCalendar?.bookedDates || {},
  });

  await updateDoc(banquetDocRef(normalizedBanquet.id), {
    status: ["pending_review", "pending_approval"].includes(currentBanquet.status)
      ? currentBanquet.status
      : "draft",
    submittedForApproval: currentBanquet.submittedForApproval || false,
    rejectionReason: currentBanquet.status === "rejected" ? currentBanquet.rejectionReason || "" : "",
    updatedAt: serverTimestamp(),
    lastEditedBy: editorEmail,
    lastEditedAt: serverTimestamp(),
    editHistory: appendEditHistory(currentBanquet.editHistory, editHistoryEntry),
  });

  return getBanquetSnapshotById(normalizedBanquet.id);
};

const submitBanquetForApprovalOnce = async ({ actingOwner, banquet }) => {
  const normalizedBanquet = normalizeBanquet(banquet);
  const existingBanquet = await getBanquetSnapshotById(normalizedBanquet.id);
  const isAlreadyLive = Boolean(existingBanquet) && ["approved", "published"].includes(existingBanquet.status);

  if (isAlreadyLive) {
    // The banquet is already public. Keep its live (approved) content and status untouched
    // so it keeps showing its old details on /banquets — only stage the new edit in
    // banquetDrafts and flag the canonical doc for admin re-review. The live doc is only
    // overwritten once the super admin approves this pending edit (see reviewBanquetSubmission).
    const editorEmail = actingOwner?.email || normalizedBanquet.ownerEmail || "";
    await saveBanquetDraft({ actingOwner, banquet, syncCanonical: false });

    await updateDoc(banquetDocRef(normalizedBanquet.id), {
      hasPendingEdit: true,
      pendingEditBy: editorEmail,
      pendingEditSubmittedAt: serverTimestamp(),
      pendingEditRejectionReason: "",
      submittedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return getBanquetSnapshotById(normalizedBanquet.id);
  }

  const updatedBanquet = await saveBanquetDraft({ actingOwner, banquet, syncCanonical: true });

  await updateDoc(banquetDocRef(updatedBanquet.id), {
    status: "pending_approval",
    submittedForApproval: true,
    submittedAt: serverTimestamp(),
    approvedAt: null,
    rejectedAt: null,
    approvedBy: "",
    rejectionReason: "",
    updatedAt: serverTimestamp(),
  });

  return getBanquetSnapshotById(updatedBanquet.id);
};

export const submitBanquetForApproval = async ({ actingOwner, banquet }) => {
  try {
    return await submitBanquetForApprovalOnce({ actingOwner, banquet });
  } catch (error) {
    // A stale ID token (e.g. right after sign-in, a password change, or a long-idle
    // tab) can cause a one-off "permission-denied" on this multi-write submit even
    // though the owner genuinely has access to their own banquet — force a fresh
    // token and retry exactly once before surfacing the failure to the owner.
    if (error?.code === "permission-denied" && auth.currentUser) {
      await auth.currentUser.getIdToken(true);
      return await submitBanquetForApprovalOnce({ actingOwner, banquet });
    }
    throw error;
  }
};

export const publishBanquetDirectly = async ({ actingOwner, banquet }) => {
  if (!canManageAllBanquets(actingOwner)) {
    const error = new Error("Only a super admin can publish banquet listings directly.");
    error.code = "banquet-owner/super-admin-only";
    throw error;
  }

  const updatedBanquet = await saveBanquetDraft({
    actingOwner,
    syncCanonical: true,
    banquet: {
      ...banquet,
      createdBy: "super_admin",
      status: "draft",
    },
  });

  await updateDoc(banquetDocRef(updatedBanquet.id), {
    status: "published",
    isActive: true,
    createdBy: "super_admin",
    submittedForApproval: false,
    approvedAt: serverTimestamp(),
    approvedBy: actingOwner.email || actingOwner.uid || "super-admin",
    rejectedAt: null,
    rejectionReason: "",
    updatedAt: serverTimestamp(),
  });

  return getBanquetSnapshotById(updatedBanquet.id);
};

export const reviewBanquetSubmission = async ({ actingOwner, banquetId, action, rejectionReason = "" }) => {
  if (!canManageAllBanquets(actingOwner)) {
    const error = new Error("Only a super admin can review banquet submissions.");
    error.code = "banquet-owner/super-admin-only";
    throw error;
  }

  const currentBanquet = await getBanquetSnapshotById(banquetId);
  if (!currentBanquet) {
    const error = new Error("Banquet not found.");
    error.code = "banquet-owner/banquet-not-found";
    throw error;
  }

  if (currentBanquet.hasPendingEdit) {
    if (action === "approve") {
      const draftSnapshot = await getDoc(banquetDraftDocRef(banquetId));
      if (!draftSnapshot.exists()) {
        const error = new Error("Pending edit draft not found.");
        error.code = "banquet-owner/draft-not-found";
        throw error;
      }

      const draftBanquet = normalizeBanquet({ id: banquetId, ...draftSnapshot.data() });

      // Reuse the same field-sync helpers a normal submission uses, so the approved
      // edit lands on the live banquet exactly like it would for a first-time approval.
      await updateBanquetBasicInfo({ actingOwner, banquet: draftBanquet });
      await updateBanquetImages({ actingOwner, banquetId, images: draftBanquet.images });
      await updateBanquetDetails({ actingOwner, banquet: draftBanquet });
      await updateBanquetAvailability({
        actingOwner,
        banquetId,
        availabilityCalendar: draftBanquet.availabilityCalendar,
        bookedSlots: draftBanquet.bookedSlots,
        bookedDates: draftBanquet.bookedDates,
      });

      await updateDoc(banquetDocRef(banquetId), {
        status: "published",
        isActive: true,
        hasPendingEdit: false,
        pendingEditBy: "",
        pendingEditSubmittedAt: null,
        pendingEditRejectionReason: "",
        approvedAt: serverTimestamp(),
        approvedBy: actingOwner.email || actingOwner.uid || "super-admin",
        rejectedAt: null,
        rejectionReason: "",
        updatedAt: serverTimestamp(),
      });
    } else {
      // Reject the pending edit only — the live banquet keeps its existing approved
      // content and status untouched, so it stays public with its old details.
      await updateDoc(banquetDocRef(banquetId), {
        hasPendingEdit: false,
        pendingEditRejectionReason: rejectionReason || "Please update your banquet profile.",
        updatedAt: serverTimestamp(),
      });
    }

    return getBanquetSnapshotById(banquetId);
  }

  if (action === "approve") {
    await updateDoc(banquetDocRef(banquetId), {
      status: "published",
      isActive: true,
      approvedAt: serverTimestamp(),
      approvedBy: actingOwner.email || actingOwner.uid || "super-admin",
      rejectedAt: null,
      rejectionReason: "",
      updatedAt: serverTimestamp(),
    });
  } else {
    await updateDoc(banquetDocRef(banquetId), {
      status: "rejected",
      rejectedAt: serverTimestamp(),
      rejectionReason: rejectionReason || "Please update your banquet profile.",
      updatedAt: serverTimestamp(),
    });
  }

  return getBanquetSnapshotById(banquetId);
};

export const updateBanquetPolicies = async ({ actingOwner, banquetId, policies }) => {
  const currentBanquet = await getBanquetSnapshotById(banquetId);

  if (!currentBanquet) {
    const error = new Error("Banquet not found.");
    error.code = "banquet-owner/banquet-not-found";
    throw error;
  }

  assertCanManageBanquet(actingOwner, currentBanquet);

  await updateDoc(banquetDocRef(banquetId), {
    policies: policies || currentBanquet.policies,
    updatedAt: serverTimestamp(),
  });

  return getBanquetSnapshotById(banquetId);
};

export const updateBanquetDetails = async ({ actingOwner, banquet }) => {
  const normalizedBanquet = normalizeBanquet(banquet);
  const currentBanquet = await getBanquetSnapshotById(normalizedBanquet.id);

  if (!currentBanquet) {
    const error = new Error("Banquet not found.");
    error.code = "banquet-owner/banquet-not-found";
    throw error;
  }

  assertCanManageBanquet(actingOwner, currentBanquet);

  await updateDoc(banquetDocRef(normalizedBanquet.id), {
    facilities: Array.isArray(banquet.facilities) ? banquet.facilities : [],
    basicDetails: normalizedBanquet.basicDetails,
    contactDetails: normalizedBanquet.contactDetails,
    locationDetails: normalizedBanquet.locationDetails,
    capacityDetails: normalizedBanquet.capacityDetails,
    pricingDetails: normalizedBanquet.pricingDetails,
    foodCatering: normalizedBanquet.foodCatering,
    decorationServices: normalizedBanquet.decorationServices,
    alcoholPolicy: normalizedBanquet.alcoholPolicy,
    alcoholPolicyText: normalizedBanquet.alcoholPolicyText,
    timingDetails: normalizedBanquet.timingDetails,
    parkingDetails: normalizedBanquet.parkingDetails,
    media: normalizedBanquet.media,
    highlights: normalizedBanquet.highlights,
    packagesMenu: normalizedBanquet.packagesMenu,
    tags: Array.isArray(banquet.tags) ? banquet.tags : [],
    eventTypes: Array.isArray(banquet.eventTypes) ? banquet.eventTypes : [],
    goodFor: Array.isArray(banquet.goodFor) ? banquet.goodFor : [],
    notIdealFor: Array.isArray(banquet.notIdealFor) ? banquet.notIdealFor : [],
    indoorOutdoor: Array.isArray(banquet.indoorOutdoor) ? banquet.indoorOutdoor : [],
    foodType: banquet.foodType || "",
    parkingAvailable: Boolean(banquet.parkingAvailable),
    parkingCapacity: numberOrZero(banquet.parkingCapacity),
    valetParking: Boolean(banquet.valetParking),
    roomsAvailable: Boolean(banquet.roomsAvailable),
    roomCount: numberOrZero(banquet.roomCount),
    bridalRoom: Boolean(banquet.bridalRoom),
    changingRoom: Boolean(banquet.changingRoom),
    acHall: Boolean(banquet.acHall),
    powerBackup: Boolean(banquet.powerBackup),
    lift: Boolean(banquet.lift),
    wheelchairFriendly: Boolean(banquet.wheelchairFriendly),
    liquorAllowed: Boolean(banquet.liquorAllowed),
    byobAllowed: Boolean(banquet.byobAllowed),
    djAllowed: Boolean(banquet.djAllowed),
    lateNightAllowed: Boolean(banquet.lateNightAllowed),
    floors: banquet.floors || "",
    outsideCateringAllowed: Boolean(banquet.outsideCateringAllowed),
    teaserVideoUrl: banquet.teaserVideoUrl || "",
    teaserVideoMeta: banquet.teaserVideoMeta || null,
    coverImageMeta: banquet.coverImageMeta || null,
    imageMetaByUrl: banquet.imageMetaByUrl || {},
    decorPolicy: banquet.decorPolicy || "",
    cateringPolicy: banquet.cateringPolicy || "",
    outsideCateringPolicy: banquet.outsideCateringPolicy || "",
    bookingPolicy: banquet.bookingPolicy || "",
    cancellationPolicy: banquet.cancellationPolicy || "",
    paymentTerms: banquet.paymentTerms || "",
    timingRules: banquet.timingRules || "",
    noiseRestrictions: banquet.noiseRestrictions || "",
    policies: {
      ...(currentBanquet.policies || {}),
      decorPolicy: banquet.decorPolicy || "",
      cateringPolicy: banquet.cateringPolicy || "",
      liquorPolicy: banquet.alcoholPolicyText || normalizedBanquet.alcoholPolicy?.notes || "",
      djPolicy: banquet.djAllowed ? "DJ allowed" : "DJ not allowed",
      lateNightPolicy: banquet.timingRules || "",
      noiseRestrictions: banquet.noiseRestrictions || "",
    },
    updatedAt: serverTimestamp(),
  });

  return getBanquetSnapshotById(normalizedBanquet.id);
};

// --- Quick Add Banquet (Super Admin) ------------------------------------

export const findBanquetByGooglePlaceId = async ({ googlePlaceId }) => {
  if (!googlePlaceId) return null;

  const snapshot = await getDocs(query(banquetCollectionRef, where("googlePlaceId", "==", googlePlaceId), limit(1)));
  if (snapshot.empty) return null;

  const item = snapshot.docs[0];
  return normalizeBanquet({ id: item.id, ...item.data() });
};

const createCallableError = (error) => {
  if (typeof error?.code === "string" && error.code.startsWith("functions/")) {
    return new Error(error.message || "Unable to complete this request.");
  }

  return error instanceof Error ? error : new Error("Unable to complete this request.");
};

export const quickAddBanquetConfirm = async ({ placeId, createForOwnerFill = false }) => {
  try {
    const callQuickAddBanquetConfirm = httpsCallable(functions, "quickAddBanquetConfirm");
    const response = await callQuickAddBanquetConfirm({ placeId, createForOwnerFill });
    return response.data;
  } catch (error) {
    throw createCallableError(error);
  }
};

// --- Owner-Fill Link (public, token-based form for owners with no login) ------

export const generateOwnerFillLink = async ({ banquetId }) => {
  try {
    const callGenerateOwnerFillLink = httpsCallable(functions, "generateOwnerFillLink");
    const response = await callGenerateOwnerFillLink({ banquetId });
    return response.data;
  } catch (error) {
    throw createCallableError(error);
  }
};

export const getBanquetByOwnerFillToken = async ({ token }) => {
  try {
    const callGetBanquetByOwnerFillToken = httpsCallable(functions, "getBanquetByOwnerFillToken");
    const response = await callGetBanquetByOwnerFillToken({ token });
    return response.data;
  } catch (error) {
    throw createCallableError(error);
  }
};

export const submitOwnerFillForm = async ({ token, banquet }) => {
  try {
    const callSubmitOwnerFillForm = httpsCallable(functions, "submitOwnerFillForm");
    const response = await callSubmitOwnerFillForm({ token, banquet });
    return response.data;
  } catch (error) {
    throw createCallableError(error);
  }
};
